import React, { useEffect, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import Icon from './Icon';

export interface ToastMessage {
  id: string;
  text: string;
  type: 'success' | 'error' | 'info';
  undoAction?: () => void;
}

let toastListeners: Array<(msg: ToastMessage) => void> = [];

// Native-only haptic cue paired with the toast so successes feel tactile and
// errors are felt before they're read. Silent no-op on web.
const fireHaptic = (type: 'success' | 'error' | 'info') => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    if (type === 'success') Haptics.notification({ type: NotificationType.Success });
    else if (type === 'error') Haptics.notification({ type: NotificationType.Error });
    else Haptics.impact({ style: ImpactStyle.Light });
  } catch { /* haptics unavailable */ }
};

export const showToast = (text: string, type: 'success' | 'error' | 'info' = 'success', undoAction?: () => void) => {
  const msg: ToastMessage = { id: Date.now().toString(), text, type, undoAction };
  fireHaptic(type);
  toastListeners.forEach(fn => fn(msg));
};

const ICONS: Record<string, { name: string; color: string }> = {
  success: { name: 'check_circle', color: '#81c784' },
  error: { name: 'error', color: '#ef5350' },
  info: { name: 'info', color: '#4fc3f7' },
};

const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((msg: ToastMessage) => {
    setToasts(prev => [...prev, msg]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== msg.id));
    }, 3500);
  }, []);

  useEffect(() => {
    toastListeners.push(addToast);
    return () => { toastListeners = toastListeners.filter(fn => fn !== addToast); };
  }, [addToast]);

  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      role="status"
      style={{
        position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
        left: 16, right: 16, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center',
        pointerEvents: 'none',
      }}
    >
      {toasts.map(toast => {
        const icon = ICONS[toast.type];
        return (
          <div
            key={toast.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 16px', borderRadius: 12, width: '100%', maxWidth: 400,
              background: 'var(--fh-dark-card)', border: '1px solid var(--fh-dark-border)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              animation: 'fh-page-enter 0.25s ease both',
              pointerEvents: 'auto',
            }}
          >
            <Icon name={icon.name} size={20} color={icon.color} />
            <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--fh-dark-text)' }}>{toast.text}</span>
            {toast.undoAction && (
              <button
                onClick={() => {
                  toast.undoAction?.();
                  setToasts(prev => prev.filter(t => t.id !== toast.id));
                }}
                style={{
                  background: 'none', border: 'none', color: '#c5512d',
                  fontWeight: 700, fontSize: 14, cursor: 'pointer', padding: '4px 8px',
                }}
              >
                Undo
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ToastContainer;
